import type { Attendance, Person } from "../types";

type ExportSection = {
  title: string;
  rows: Attendance[];
};

const cleanSheetName = (value: string) =>
  (value || "考勤").replace(/[\\/*?:[\]]/g, "_").slice(0, 25) || "考勤";

const cleanFileName = (value: string) =>
  (value || "项目").replace(/[\\/:*?"<>|\s]+/g, "_");

function nextSheetName(used: Set<string>, base: string, page: number) {
  const initial = cleanSheetName(`${base}${page > 1 ? page : ""}`);
  let name = initial;
  let suffix = 2;
  while (used.has(name)) name = cleanSheetName(`${initial}_${suffix++}`);
  used.add(name);
  return name;
}

function copyTemplateSheet(workbook: any, template: any, name: string) {
  const sheet = workbook.addWorksheet(name, {
    properties: { ...template.properties },
    pageSetup: { ...template.pageSetup },
    views: template.views ? template.views.map((view: unknown) => ({ ...(view as object) })) : undefined,
  });
  sheet.pageSetup = { ...template.pageSetup };
  sheet.headerFooter = { ...template.headerFooter };
  sheet.pageMargins = template.pageMargins ? { ...template.pageMargins } : undefined;
  for (let col = 1; col <= 38; col += 1) {
    const sourceColumn = template.getColumn(col);
    const targetColumn = sheet.getColumn(col);
    targetColumn.width = sourceColumn.width;
    targetColumn.hidden = sourceColumn.hidden;
    targetColumn.outlineLevel = sourceColumn.outlineLevel;
  }
  for (let row = 1; row <= 43; row += 1) {
    const sourceRow = template.getRow(row);
    const targetRow = sheet.getRow(row);
    targetRow.height = sourceRow.height;
    targetRow.hidden = sourceRow.hidden;
    for (let col = 1; col <= 38; col += 1) {
      const source = sourceRow.getCell(col);
      const target = targetRow.getCell(col);
      target.value = source.value;
      // ExcelJS 读取模板后没有可靠的 hasStyle 标志，必须无条件深拷贝样式。
      // 否则会退回 Calibri、无边框和默认对齐。
      target.style = JSON.parse(JSON.stringify(source.style || {}));
      if (source.note) target.note = source.note;
    }
  }
  const merges = ((template.model as { merges?: string[] }).merges || []);
  merges.forEach(range => sheet.mergeCells(range));
  sheet.properties = { ...template.properties };
  sheet.pageSetup = { ...template.pageSetup };
  sheet.autoFilter = template.autoFilter ? { ...template.autoFilter } : undefined;
  return sheet;
}

function attendanceSections(projectName: string, attendance: Attendance[]) {
  const sections: ExportSection[] = [{ title: "项目汇总", rows: attendance }];
  const areas = [...new Set(attendance.map(row => String(row.area || "未分区域").trim() || "未分区域"))]
    .sort((a, b) => a.localeCompare(b, "zh-CN"));
  areas.forEach(area => sections.push({
    title: area,
    rows: attendance.filter(row => (String(row.area || "未分区域").trim() || "未分区域") === area),
  }));
  return sections.map(section => ({ ...section, title: section.title || projectName }));
}

function sectionPeople(rows: Attendance[], people: Person[]) {
  const personMap = new Map(people.map(person => [person.id, person]));
  const ids: string[] = [];
  rows.forEach(row => {
    if (!ids.includes(row.person_id)) ids.push(row.person_id);
  });
  return ids.map(id => personMap.get(id)).filter(Boolean) as Person[];
}

function fillPerson(sheet: any, rowStart: number, person: Person, rows: Attendance[]) {
  sheet.getCell(rowStart, 1).value = Math.floor((rowStart - 6) / 3) + 1;
  sheet.getCell(rowStart, 2).value = person.name;
  const grouped = new Map<number, { regular: number; overtime: number; remarks: string[] }>();
  rows.forEach(row => {
    const day = Number(String(row.work_date || "").slice(8, 10));
    if (!day || day > 31) return;
    const item = grouped.get(day) || { regular: 0, overtime: 0, remarks: [] };
    item.regular += Number(row.regular_hours || 0);
    item.overtime += Number(row.overtime_hours || 0);
    if (row.remark) item.remarks.push(String(row.remark));
    grouped.set(day, item);
  });
  let totalRegular = 0;
  let totalOvertime = 0;
  grouped.forEach((item, day) => {
    const col = day + 3;
    const regular = Math.max(0, Math.min(8, item.regular));
    totalRegular += regular;
    totalOvertime += Math.max(0, item.overtime);
    if (regular > 0) {
      const afternoonOnly = regular <= 4 && item.remarks.some(value => value.includes("下午"));
      if (!afternoonOnly) sheet.getCell(rowStart, col).value = "√";
      if (regular > 4 || afternoonOnly) sheet.getCell(rowStart + 1, col).value = "√";
    }
    if (item.overtime > 0) sheet.getCell(rowStart + 2, col).value = item.overtime;
  });
  sheet.getCell(rowStart, 35).value = Number((totalRegular / 8).toFixed(2));
  sheet.getCell(rowStart, 36).value = person.trade || "";
  const rate = Number(person.rate || 0);
  const overtimeRate = Number(person.overtime_rate || 0);
  if (rate > 0 || overtimeRate > 0) {
    const baseWage = String(person.salary_type || "日薪").includes("月")
      ? (totalRegular > 0 ? rate : 0)
      : (totalRegular / 8) * rate;
    sheet.getCell(rowStart, 37).value = Number((baseWage + totalOvertime * overtimeRate).toFixed(2));
    sheet.getCell(rowStart, 37).numFmt = "#,##0.00";
  }
}

export async function buildHuayueAttendanceXlsx(
  templateBytes: ArrayBuffer,
  projectName: string,
  month: string,
  attendance: Attendance[],
  people: Person[],
) {
  if (!/^\d{4}-\d{2}$/.test(month)) throw new Error("报表月份格式不正确");
  if (!attendance.length) throw new Error("当前项目本月没有考勤记录");
  const ExcelJS = (await import("exceljs")).default;
  const source = new ExcelJS.Workbook();
  await source.xlsx.load(templateBytes);
  const template = source.worksheets[0];
  if (!template) throw new Error("华粤现场考勤模板不存在");
  const output = new ExcelJS.Workbook();
  output.creator = "工时记";
  output.created = new Date();
  const used = new Set<string>();
  const [year, monthNumber] = month.split("-").map(Number);
  for (const section of attendanceSections(projectName, attendance)) {
    const sectionPeopleRows = sectionPeople(section.rows, people);
    const pageCount = Math.max(1, Math.ceil(sectionPeopleRows.length / 12));
    for (let page = 0; page < pageCount; page += 1) {
      const sheet = copyTemplateSheet(output, template, nextSheetName(used, section.title, page + 1));
      sheet.getCell("A2").value = `项目名称：${projectName}`;
      sheet.getCell("A3").value = `施工队名称：${section.title}`;
      sheet.getCell("C4").value = `${year}年 ${monthNumber} 月`;
      const selected = sectionPeopleRows.slice(page * 12, page * 12 + 12);
      selected.forEach((person, index) => fillPerson(
        sheet,
        6 + index * 3,
        person,
        section.rows.filter(row => row.person_id === person.id),
      ));
    }
  }
  return output.xlsx.writeBuffer();
}

export async function downloadHuayueAttendanceXlsx(
  projectName: string,
  month: string,
  attendance: Attendance[],
  people: Person[],
) {
  const response = await fetch("/templates/attendance_template_huayue.xlsx", { cache: "no-store" });
  if (!response.ok) throw new Error("华粤现场考勤模板读取失败");
  const buffer = await buildHuayueAttendanceXlsx(await response.arrayBuffer(), projectName, month, attendance, people);
  const blob = new Blob([buffer as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `华粤现场考勤表_${cleanFileName(projectName)}_${month}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
}
